/**
 * "1–50 of 213" + Previous/Next under a list. Renders nothing when
 * everything fits on one page. List endpoints cap a page at 50 rows by
 * default, so without this anything past the first page is unreachable.
 *
 * A page can stop existing (a shared link, payments that settled out of
 * a filter). The server then answers 404, so go back to the first page
 * instead of leaving the operator on a false "no results".
 */
import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PaginatedResponse } from "@/lib/api";
import { isMissingPage } from "@/lib/use-page-param";

export function ListPagination<T>({
  query,
  page,
  onPage,
}: {
  query: { data?: PaginatedResponse<T>; error: unknown; isPlaceholderData: boolean };
  page: number;
  onPage: (next: number) => void;
}) {
  const { data, error, isPlaceholderData } = query;
  const pageIsGone = isMissingPage(error, page);
  useEffect(() => {
    if (pageIsGone) onPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onPage is rebuilt every render
  }, [pageIsGone]);

  if (!data || (page <= 1 && !data.has_next)) return null;
  const first = (data.page - 1) * data.page_size + 1;
  const last = first + data.results.length - 1;
  return (
    <nav
      aria-label="Pagination"
      className="mt-3 flex items-center justify-between gap-3 text-xs text-muted-foreground"
    >
      <span className="tabular-nums">
        {data.results.length > 0 ? `${first}–${last} of ${data.total}` : `${data.total} total`}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!data.has_next || isPlaceholderData}
          onClick={() => onPage(page + 1)}
        >
          Next
          <ChevronRight />
        </Button>
      </div>
    </nav>
  );
}
